import apiClient from './api';

class LifeDeclarationService {
  /**
   * Get all users with due life declarations
   * @param {Object} params - Query parameters
   * @param {string} params.demise_status - Filter by: alive, presumed_deceased, confirmed_deceased
   * @param {string} params.life_cycle_status - Filter by lifecycle (e.g. open, closed). If not provided, shows both.
   * @param {number} params.min_failures - Minimum failure count (default: 1)
   * @param {boolean} params.notification_sent - Filter by nominee notification status
   * @param {boolean} params.has_certificate - Filter by death certificate existence
   * @param {string} params.search - Search by user email or name
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page (max: 100)
   * @returns {Promise} - Paginated list of due life declarations
   */
  async getDueLifeDeclarations(params = {}) {
    try {
      const response = await apiClient.get('/admin/due-life-declarations/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching due life declarations:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get user details for a specific due life declaration
   * @param {string|number} id - User identifier
   * @returns {Promise} - User details with life declaration history
   */
  async getUserDetails(id) {
    try {
      const response = await apiClient.get(`/admin/due-life-declarations/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      return this.handleError(error);
    }
  }

  /**
   * Take admin action on a due life declaration (Unified Endpoint)
   * Available action types:
   * - mark_life_declaration: Submit life declaration on behalf of user (file field: attachment_file)
   * - mark_demise_confirmation: Confirm user demise (file field: supporting_doc_file)
   * - upload_death_certificate: Upload death certificate (file field: certificate_file - REQUIRED)
   * - preview: Get list of nominees eligible for PDF preview
   * - generate_share: Generate and share documents with nominees (file field: supporting_doc_file)
   *
   * @param {string|number} id - User identifier
   * @param {Object} data - Action data
   * @param {string} data.action_type - Action type (required)
   * @param {string} data.admin_remarks - Admin remarks
   * @param {File} data.certificate_file - Certificate file (required for upload_death_certificate)
   * @param {File} data.attachment_file - Attachment file (optional for mark_life_declaration - medical receipts, doctor's notes)
   * @param {File} data.supporting_doc_file - Supporting document file (optional for mark_demise_confirmation, generate_share - obituaries, will, etc.)
   * @returns {Promise} - Action confirmation
   */
  async takeAction(id, data) {
    try {
      const { action_type, admin_remarks, certificate_file, attachment_file, supporting_doc_file } = data;

      // For upload_death_certificate, use multipart/form-data
      if (action_type === 'upload_death_certificate' && certificate_file) {
        const formData = new FormData();
        formData.append('action_type', action_type);
        formData.append('certificate_file', certificate_file);
        if (admin_remarks) {
          formData.append('admin_remarks', admin_remarks);
        }

        const response = await apiClient.post(
          `/admin/due-life-declarations/${id}/action/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return {
          success: true,
          data: response.data,
        };
      }

      // For preview action, get nominees list (JSON response)
      if (action_type === 'preview') {
        const response = await apiClient.post(
          `/admin/due-life-declarations/${id}/action/`,
          { action_type }
        );

        return {
          success: true,
          data: response.data,
        };
      }

      // For mark_life_declaration with attachment_file, use multipart/form-data
      if (action_type === 'mark_life_declaration' && attachment_file) {
        const formData = new FormData();
        formData.append('action_type', action_type);
        formData.append('attachment_file', attachment_file);
        if (admin_remarks) {
          formData.append('admin_remarks', admin_remarks);
        }

        const response = await apiClient.post(
          `/admin/due-life-declarations/${id}/action/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return {
          success: true,
          data: response.data,
        };
      }

      // For mark_demise_confirmation or generate_share with supporting_doc_file, use multipart/form-data
      if ((action_type === 'mark_demise_confirmation' || action_type === 'generate_share') && supporting_doc_file) {
        const formData = new FormData();
        formData.append('action_type', action_type);
        formData.append('supporting_doc_file', supporting_doc_file);
        if (admin_remarks) {
          formData.append('admin_remarks', admin_remarks);
        }

        const response = await apiClient.post(
          `/admin/due-life-declarations/${id}/action/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return {
          success: true,
          data: response.data,
        };
      }

      // For other actions (or actions without files), use JSON
      const payload = {
        action_type,
      };
      if (admin_remarks) {
        payload.admin_remarks = admin_remarks;
      }

      const response = await apiClient.post(`/admin/due-life-declarations/${id}/action/`, payload);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error taking action:', error);
      return this.handleError(error);
    }
  }

  /**
   * Upload death certificate for a user (uses unified action endpoint)
   * @param {string|number} id - User identifier
   * @param {File} certificateFile - Certificate file (PDF, JPG, PNG)
   * @param {Object} options - Additional options
   * @param {string} options.admin_remarks - Admin remarks
   * @returns {Promise} - Upload confirmation
   */
  async uploadCertificate(id, certificateFile, options = {}) {
    return this.takeAction(id, {
      action_type: 'upload_death_certificate',
      certificate_file: certificateFile,
      admin_remarks: options.admin_remarks,
    });
  }

  /**
   * Get PDF preview for a specific nominee
   * @param {string} userId - User identifier
   * @param {string} nomineeId - Nominee identifier
   * @returns {Promise} - PDF URL and metadata
   */
  async getNomineePdfPreview(userId, nomineeId) {
    try {
      const response = await apiClient.get(
        `/admin/due-life-declarations/${userId}/nominees/${nomineeId}/preview-pdf/`
      );

      // Response contains pdf_url and metadata
      if (response.data && response.data.pdf_url) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching nominee PDF preview:', error);
      return this.handleError(error);
    }
  }

  /**
   * Fetch a nominee preview PDF as a blob via authenticated proxy
   * @param {string} pdfProxyUrl - Proxy URL path (e.g. "/api/nominees/{id}/download-preview-pdf/")
   * @returns {Promise} - Blob URL for the PDF
   */
  async getPreviewPdfBlob(pdfProxyUrl) {
    try {
      // Strip leading /api if present since apiClient already has /api as base
      const cleanUrl = pdfProxyUrl.replace(/^\/api/, '');
      const response = await apiClient.get(cleanUrl, {
        responseType: 'blob',
        params: { inline: 'true' },
      });
      const blobUrl = URL.createObjectURL(response.data);
      return {
        success: true,
        data: { blobUrl, mimeType: response.data.type },
      };
    } catch (error) {
      console.error('Error fetching preview PDF blob:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      const { status, data } = error.response;

      // Handle specific status codes first
      if (status === 500) {
        message = 'Server error. Please try again later.';
      } else if (status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (status === 404) {
        message = 'User not found.';
      } else if (status === 401) {
        message = 'Session expired. Please login again.';
      } else if (data) {
        // Check if response is HTML (server error page)
        if (typeof data === 'string' && data.includes('<!doctype html>')) {
          message = `Server Error (${status}). Please try again later.`;
        } else if (typeof data === 'string') {
          message = data;
        } else if (data.detail) {
          message = data.detail;
        } else if (data.error) {
          message = data.error;
        } else if (data.message) {
          message = data.message;
        } else {
          const firstKey = Object.keys(data)[0];
          if (firstKey && Array.isArray(data[firstKey])) {
            message = data[firstKey][0];
          }
        }
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new LifeDeclarationService();
